import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import i18n from "../i18n/i18n";

// ========================================
// TYPES
// ========================================

/**
 * The centre a pub is attached to.
 *
 * Arrives eager-loaded on every pub (App\Services\Pub::SIGN_COLUMNS). The table
 * has always read `data.sign?.name`, but the API never loaded the relation, so
 * the SIGN column rendered "General" for every row including assigned ones -
 * the assignment was completely invisible from the back-office.
 */
export interface PubSign {
  id: number;
  name: string | null;
  centre_code: string | null;
}

/**
 * L'enseigne (réseau) à laquelle une campagne est vendue.
 *
 * Eager-loadée sur chaque pub (App\Services\Pub::ETABLISSEMENT_COLUMNS), au même
 * titre que `sign`. À ne PAS confondre avec `sign.etablissement` : celle-ci dit
 * « cette campagne couvre tous les centres de ce réseau », l'autre dirait
 * seulement « le centre auquel elle est rattachée appartient à ce réseau ».
 */
export interface PubEtablissement {
  id: number;
  name: string | null;
}

/**
 * À QUI la campagne est vendue. Calculé par l'API (accessor `attachment_scope`).
 *
 * Lu tel quel plutôt que redéduit d'un `sign_id == null` : ce test-là était
 * juste tant que le rattachement réseau n'existait pas, et il devient faux
 * ensuite — une campagne vendue à une enseigne entière n'a pas de `sign_id` et
 * se serait donc affichée comme une campagne plateforme, au mauvais annonceur
 * et au mauvais tarif.
 *
 * Indépendant de `is_nationwide`, qui parle de GÉOGRAPHIE (à qui la pub est
 * servie selon sa position), pas de rattachement commercial.
 */
export type PubAttachmentScope = 'centre' | 'network' | 'general';

export interface Pub {
  id: number;
  name: string | null;
  description: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
  /**
   * Rayon de diffusion, en kilomètres.
   *
   * 50 par défaut — c'est la règle produit, portée par la colonne et par
   * `Pub::DEFAULT_RADIUS_KM` côté API, pas par ce fichier. `null` signifie
   * explicitement « sans limite de distance » et n'est PAS équivalent à une
   * pub sans coordonnées : la première reste triée par proximité, la seconde
   * n'a pas de position du tout.
   */
  radius_km: number | null;
  main_image: string | null;
  gallery_images: string[] | null;
  pub_type: 'free' | 'premium' | 'sponsored';
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  display_order: number | null;
  sign_id: number | null;
  sign?: PubSign | null;
  /** Enseigne à laquelle la campagne est vendue, ou null. */
  etablissement_id: number | null;
  etablissement?: PubEtablissement | null;
  /** Portée de vente, décidée par l'API. Voir PubAttachmentScope. */
  attachment_scope: PubAttachmentScope;
  user_id: number | null;
  is_pub_active: boolean;
  /**
   * Pas de coordonnées -> la campagne est diffusée partout.
   *
   * Calculé par l'API (accessor `is_nationwide`) plutôt que re-deviné ici par
   * `latitude == null`. La règle n'admet qu'une définition, et elle est
   * partagée avec l'application mobile.
   */
  is_nationwide: boolean;
  /**
   * Distance en km, présente UNIQUEMENT quand la requête portait lat/lng.
   * `null` sur une campagne sans coordonnées (voir `is_nationwide`).
   */
  distance_km?: number | null;
  created_at: string;
  updated_at: string;
}

/** Plafond de saisie du rayon. Doit rester aligné sur `Pub::MAX_RADIUS_KM`. */
export const PUB_MAX_RADIUS_KM = 500;

/** Défaut appliqué côté base. Aligné sur `Pub::DEFAULT_RADIUS_KM`. */
export const PUB_DEFAULT_RADIUS_KM = 50;

export interface CreatePubRequest {
  name?: string;
  description?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  /**
   * `null` DIFFUSE SANS LIMITE de distance ; `undefined` laisse la valeur
   * inchangée ; un nombre fixe le rayon.
   *
   * Même piège que `sign_id` ci-dessous : envoyer `radius_km || undefined`
   * ferait disparaître la clé du corps JSON et le `fill()` côté API ne
   * remettrait jamais la colonne à NULL — le rayon serait élargissable mais
   * jamais levé.
   */
  radius_km?: number | null;
  main_image?: string;
  gallery_images?: string[];
  pub_type?: 'free' | 'premium' | 'sponsored';
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
  display_order?: number;
  /**
   * `null` DETACHES the pub from its centre; `undefined` leaves it untouched;
   * a number attaches it.
   *
   * The three-way distinction is the whole reason this is spelled out rather
   * than left as `number`: the edit form used to send `sign_id: formData.sign_id
   * || undefined`, so choosing "General Pub (No Sign)" dropped the key from the
   * JSON body entirely and the backend's fill() never cleared the column. A pub
   * could be attached to a centre but never detached again from the UI.
   *
   * It lives on CreatePubRequest (rather than only on the Update variant) so
   * both share one shape - an Update interface that widens a Create field
   * cannot extend Partial<Create>, and splitting them would let the two drift.
   */
  sign_id?: number | null;
  /**
   * `null` DÉTACHE la campagne de son enseigne ; `undefined` laisse la valeur
   * inchangée ; un nombre la rattache.
   *
   * Exactement le même contrat — et le même piège — que `sign_id` ci-dessus :
   * `formData.etablissement_id || undefined` ferait disparaître la clé du corps
   * JSON et le `fill()` côté API ne remettrait jamais la colonne à NULL. Une
   * campagne serait rattachable à un réseau et jamais détachable depuis l'UI.
   *
   * ⚠️ L'API refuse (422) le couple `sign_id` + `etablissement_id` quand le
   * centre n'appartient PAS à cette enseigne — voir
   * PubController::attachmentConflict.
   */
  etablissement_id?: number | null;
  user_id?: number;
}

export interface UpdatePubRequest extends Partial<CreatePubRequest> {}

/**
 * The sentinel `sign_id` value meaning "not attached to any centre".
 *
 * Must stay in sync with Pub::SIGN_FILTER_GENERAL on the API. Exported so no
 * component re-types the literal - the previous "general" was typed twice and
 * one of the two was then destroyed by a Number() cast.
 */
export const SIGN_FILTER_GENERAL = 'general' as const;

/** "Attached to any centre", the complement of SIGN_FILTER_GENERAL. */
export const SIGN_FILTER_ASSIGNED = 'assigned' as const;

export type SignFilterValue =
  | number
  | typeof SIGN_FILTER_GENERAL
  | typeof SIGN_FILTER_ASSIGNED;

/**
 * Sentinelles du filtre enseigne.
 *
 * Volontairement les MÊMES chaînes que côté centre (Pub::ETABLISSEMENT_FILTER_GENERAL
 * reprend la valeur de SIGN_FILTER_GENERAL) : les deux filtres cohabitent dans
 * la même barre d'outils et un opérateur n'a pas à apprendre deux vocabulaires
 * pour la même idée.
 */
export type EtablissementFilterValue =
  | number
  | typeof SIGN_FILTER_GENERAL
  | typeof SIGN_FILTER_ASSIGNED;

export interface PubFilters {
  city?: string;
  pub_type?: 'free' | 'premium' | 'sponsored';
  search?: string;
  limit?: number;
  /**
   * A centre id, or one of the two sentinels. `sign_id` was the single most
   * used filter on this screen and was MISSING from this interface, so the page
   * passed it through a spread and TypeScript never checked it.
   */
  sign_id?: SignFilterValue;
  /** Compare several centres at once. */
  sign_ids?: number[];
  /**
   * Une enseigne, ou une sentinelle. Se combine en ET avec `sign_id` : « les
   * campagnes du réseau X qui ne sont rattachées à aucun centre » est
   * précisément la question d'un commercial qui audite ce qu'il a vendu au
   * réseau.
   */
  etablissement_id?: EtablissementFilterValue;
  /** Comparer plusieurs enseignes d'un coup. */
  etablissement_ids?: number[];
  /**
   * Include pubs that are switched off or outside their date window. The API
   * defaults to active-only because the mobile app shares this endpoint; the
   * back-office needs everything, so it opts in.
   */
  include_inactive?: boolean;
  page?: number;
  per_page?: number;
}

export interface PubStats {
  total: number;
  active: number;
  inactive: number;
  free: number;
  premium: number;
  sponsored: number;
  assigned: number;
  general: number;
  /**
   * Vendues à une enseigne entière, sans centre précis.
   *
   * `assigned + network + platform === total`. `general` NE fait PAS partie de
   * cette identité : il ne teste que `sign_id` et englobe donc `network`, parce
   * qu'il décrit ce que le flux mobile sert réellement (voir Pub::scopeGeneral).
   */
  network: number;
  /** Ni centre ni enseigne : la plateforme au sens strict. */
  platform: number;
  /** Campagnes sans coordonnées, donc diffusées partout. */
  nationwide: number;
  /** Campagnes qui portent des coordonnées et sont donc soumises au rayon. */
  geo_targeted: number;
  /** Rayon par défaut servi par l'API, pour ne pas coder 50 en dur dans l'UI. */
  default_radius_km: number;
  /**
   * Distinct cities across the WHOLE catalogue, ignoring the current filters.
   *
   * Served from here rather than derived client-side because the page used to
   * build its city dropdown from the already-city-filtered rows, which collapsed
   * the dropdown to one entry as soon as a city was picked.
   */
  cities: string[];
  /**
   * Les enseignes qui portent au moins une campagne, pour le menu du filtre.
   *
   * Calculées sur tout le catalogue et non sur les lignes affichées — même
   * raison que `cities` : une liste d'options dérivée des résultats de son
   * propre filtre s'effondre à une seule entrée dès qu'on s'en sert.
   */
  etablissements: PubEtablissement[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

// ========================================
// QUERY KEYS
// ========================================

export const pubKeys = {
  all: ['pubs'] as const,
  lists: () => [...pubKeys.all, 'list'] as const,
  list: (filters?: PubFilters) => [...pubKeys.lists(), filters] as const,
  stats: (filters?: PubFilters) => [...pubKeys.all, 'stats', filters] as const,
  general: (filters?: Pick<PubFilters, 'city' | 'limit'>) => 
    [...pubKeys.all, 'general', filters] as const,
  bySign: (signId: number) => [...pubKeys.all, 'by-sign', signId] as const,
  byEtablissement: (etablissementId: number) =>
    [...pubKeys.all, 'by-etablissement', etablissementId] as const,
  details: () => [...pubKeys.all, 'detail'] as const,
  detail: (id: number) => [...pubKeys.details(), id] as const,
};

// ========================================
// QUERIES
// ========================================

/**
 * Fetch all pubs with optional filters
 */
export const useGetAllPubs = (filters?: PubFilters) => {
  const { data, error, ...res } = useQuery({
    queryKey: pubKeys.list(filters),
    queryFn: async (): Promise<Pub[]> => {
      const response = await axios.get<ApiResponse<Pub[]>>('/pub', {
        params: filters
      });
      /*
       * Defend the shape.
       *
       * /pub answers `{success, data}` where `data` is an array when unpaginated
       * and `{data, pagination}` when paginated - and, before the service was
       * fixed, an ERROR OBJECT with HTTP 200 when the query blew up. Handing any
       * of those non-arrays to the table produced a blank grid indistinguishable
       * from an empty catalogue. Normalise here, once.
       */
      const payload = response.data?.data as unknown;

      if (Array.isArray(payload)) return payload as Pub[];

      const nested = (payload as { data?: unknown })?.data;
      if (Array.isArray(nested)) return nested as Pub[];

      return [];
    },
  });
  return { pubs: data ?? [], error, ...res };
};

/**
 * Catalogue-wide counts for the summary cards.
 *
 * Deliberately NOT derived from useGetAllPubs' rows: those are filtered (and,
 * once pagination is on, truncated), so counting them made a card labelled
 * "Total Pubs" report the size of the current filter instead.
 */
export const useGetPubStats = (filters?: PubFilters) => {
  const { data, error, ...res } = useQuery({
    queryKey: pubKeys.stats(filters),
    queryFn: async (): Promise<PubStats> => {
      const response = await axios.get<ApiResponse<PubStats>>('/pub/stats', {
        params: filters,
      });
      return response.data.data;
    },
  });
  return { stats: data, error, ...res };
};

/**
 * Fetch general pubs (not linked to any garage)
 */
export const useGetGeneralPubs = (filters?: Pick<PubFilters, 'city' | 'limit'>) => {
  const { data, error, ...res } = useQuery({
    queryKey: pubKeys.general(filters),
    queryFn: async (): Promise<Pub[]> => {
      const response = await axios.get<ApiResponse<Pub[]>>('/pub/general', {
        params: filters
      });
      return response.data.data;
    },
  });
  return { pubs: data ?? [], error, ...res };
};

/**
 * Fetch pubs by sign (garage)
 *
 * ⚠️ Renvoie les campagnes du centre **ET** celles de son enseigne (voir
 * App\Services\Pub::getBySign) : c'est le rattachement réseau, et c'est ce qui
 * remplace les N copies d'une même campagne, une par centre.
 *
 * `includeNetwork = false` revient aux seules campagnes explicitement
 * rattachées à CE centre — ce dont un écran d'édition a besoin : y montrer les
 * campagnes du réseau ferait croire à l'opérateur qu'il peut les détacher
 * depuis la fiche du centre, ce qui est faux.
 */
export const useGetPubsBySign = (
  signId: number,
  enabled = true,
  includeNetwork = true,
) => {
  const { data, error, ...res } = useQuery({
    queryKey: [...pubKeys.bySign(signId), { includeNetwork }] as const,
    queryFn: async (): Promise<Pub[]> => {
      const response = await axios.get<ApiResponse<Pub[]>>(`/pub/sign/${signId}`, {
        // `0`, pas `false` : la valeur traverse une query string, et l'API
        // normalise "0"/"1" (PubController::BOOLEAN_FILTERS). `false` y
        // arriverait comme la chaîne "false", que la règle `boolean` de Laravel
        // refuse — un 422 sur un paramètre purement optionnel.
        params: includeNetwork ? undefined : { include_network: 0 },
      });
      return response.data.data;
    },
    enabled: enabled && !!signId,
  });
  return { pubs: data ?? [], error, ...res };
};

/**
 * Les campagnes vendues à une enseigne entière.
 *
 * Route dédiée plutôt que `useGetAllPubs({ etablissement_id })` : un identifiant
 * dans le chemin ne peut pas être confondu avec une sentinelle, alors que
 * `etablissement_id=general` est un filtre parfaitement légitime.
 */
export const useGetPubsByEtablissement = (etablissementId: number, enabled = true) => {
  const { data, error, ...res } = useQuery({
    queryKey: pubKeys.byEtablissement(etablissementId),
    queryFn: async (): Promise<Pub[]> => {
      const response = await axios.get<ApiResponse<Pub[]>>(
        `/pub/etablissement/${etablissementId}`,
      );
      return response.data.data;
    },
    enabled: enabled && !!etablissementId,
  });
  return { pubs: data ?? [], error, ...res };
};

/**
 * Fetch single pub
 */
export const useGetPub = (id: number, enabled = true) => {
  const { data, error, ...res } = useQuery({
    queryKey: pubKeys.detail(id),
    queryFn: async (): Promise<Pub> => {
      const response = await axios.get<ApiResponse<Pub>>(`/pub/${id}`);
      return response.data.data;
    },
    enabled: enabled && !!id,
  });
  return { pub: data, error, ...res };
};

// ========================================
// MUTATIONS
// ========================================

/**
 * Create new pub
 */
export const useCreatePub = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreatePubRequest): Promise<Pub> => {
      const response = await axios.post<ApiResponse<Pub>>('/pub', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      // Invalidate all pub lists
      queryClient.invalidateQueries({ queryKey: pubKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pubKeys.general() });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'stats'] });
      
      // If pub is linked to a sign, invalidate that sign's pubs
      if (data.sign_id) {
        queryClient.invalidateQueries({ queryKey: pubKeys.bySign(data.sign_id) });
      }

      /*
       * Une campagne réseau apparaît dans la fiche de CHAQUE centre de
       * l'enseigne (App\Services\Pub::getBySign). On ne peut donc pas cibler les
       * clés à invalider sans connaître la liste des centres du réseau — une
       * requête supplémentaire pour économiser un refetch qui, lui, est gratuit
       * tant que personne ne regarde ces écrans. On les lâche en bloc.
       */
      if (data.etablissement_id) {
        queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'by-sign'] });
        queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'by-etablissement'] });
      }
    },
    onError: (error: any) => {
      console.error('Error creating pub:', error);
    }
  });
};

/**
 * Update pub
 */
export const useUpdatePub = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdatePubRequest }): Promise<Pub> => {
      const response = await axios.put<ApiResponse<Pub>>(`/pub/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate all pub lists
      queryClient.invalidateQueries({ queryKey: pubKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pubKeys.general() });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'stats'] });
      
      // Invalidate specific pub detail
      queryClient.invalidateQueries({ queryKey: pubKeys.detail(variables.id) });
      
      /*
       * Invalidate BOTH centres, not just the new one.
       *
       * Reassigning a pub from centre A to centre B leaves A's cached list
       * showing a pub it no longer owns. Only `data.sign_id` (the new centre)
       * was invalidated, so the old centre's list stayed stale until a hard
       * refresh - and if the pub was DETACHED, data.sign_id is null and nothing
       * at all was invalidated. `bySign` keys are cheap to drop wholesale.
       */
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'by-sign'] });
      // Même raison, côté réseau : déplacer une campagne d'une enseigne à une
      // autre périme les deux listes, et la détacher n'en laisse aucune à
      // cibler.
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'by-etablissement'] });
    },
    onError: (error: any) => {
      console.error('Error updating pub:', error);
    }
  });
};

/**
 * Delete pub
 */
export const useDeletePub = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`/pub/${id}`);
    },
    onSuccess: (_, id) => {
      // Invalidate all pub lists and queries
      queryClient.invalidateQueries({ queryKey: pubKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pubKeys.general() });
      queryClient.invalidateQueries({ queryKey: pubKeys.all });
      
      // Remove specific pub from cache
      queryClient.removeQueries({ queryKey: pubKeys.detail(id) });
    },
    onError: (error: any) => {
      console.error('Error deleting pub:', error);
    }
  });
};

/**
 * Add image to pub gallery
 */
export const useAddGalleryImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, imageUrl }: { id: number; imageUrl: string }): Promise<Pub> => {
      const response = await axios.post<ApiResponse<Pub>>(`/pub/${id}/gallery`, {
        image_url: imageUrl
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      // Update specific pub in cache
      queryClient.invalidateQueries({ queryKey: pubKeys.detail(variables.id) });
    },
    onError: (error: any) => {
      console.error('Error adding gallery image:', error);
    }
  });
};

/**
 * Delete image from pub gallery
 */
export const useDeleteGalleryImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, imageUrl }: { id: number; imageUrl: string }): Promise<Pub> => {
      const response = await axios.delete<ApiResponse<Pub>>(`/pub/${id}/gallery`, {
        data: { image_url: imageUrl }
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      // Update specific pub in cache
      queryClient.invalidateQueries({ queryKey: pubKeys.detail(variables.id) });
    },
    onError: (error: any) => {
      console.error('Error deleting gallery image:', error);
    }
  });
};

/**
 * Assign pub to sign (garage)
 *
 * PATH: `/pub/...`, singular.
 *
 * This hook and the three below it were built against `/pubs/{id}/assign-sign`,
 * `/pubs/{id}/unassign-sign`, `/pubs/{id}/toggle-active` and
 * `/pubs/{id}/gallery`. None of those routes exist - routes/api/pub.php
 * registers `/pub/{id}/assign`, `/pub/{id}/unassign`,
 * `/pub/{id}/toggle-active` and `/pub/{id}/gallery`. Every call 404'd, and
 * because each onError only console.error'd, the operator saw nothing at all:
 * no toast, no change, no clue. Assignment-by-centre has therefore never
 * worked from these hooks; the edit modal's sign_id field was the only path
 * that reached the server.
 */
export const useAssignPubToSign = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, signId }: { id: number; signId: number }): Promise<Pub> => {
      const response = await axios.post<ApiResponse<Pub>>(`/pub/${id}/assign`, {
        sign_id: signId
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: pubKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: pubKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pubKeys.general() });
      queryClient.invalidateQueries({ queryKey: pubKeys.bySign(variables.signId) });
      // Moving a pub between centres changes the assigned/general split.
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'stats'] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ??
          i18n.t('pub.assignError', "Failed to assign the ad to the centre")
      );
    }
  });
};

/**
 * Unassign pub from sign (make it general)
 */
export const useUnassignPubFromSign = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number): Promise<Pub> => {
      const response = await axios.post<ApiResponse<Pub>>(`/pub/${id}/unassign`);
      return response.data.data;
    },
    onSuccess: (data, id) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: pubKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: pubKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pubKeys.general() });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'stats'] });
      
      // If pub was linked to a sign, invalidate that sign's pubs
      if (data.sign_id) {
        queryClient.invalidateQueries({ queryKey: pubKeys.bySign(data.sign_id) });
      }
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ??
          i18n.t('pub.unassignError', "Failed to detach the ad from its centre")
      );
    }
  });
};

/**
 * Rattache la campagne à une enseigne entière.
 *
 * L'API détache le centre au passage (App\Services\Pub::assignToEtablissement) :
 * les deux niveaux sont exclusifs du point de vue de la VENTE, donc cette
 * mutation ne peut pas produire de couple contradictoire.
 */
export const useAssignPubToEtablissement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      etablissementId,
    }: {
      id: number;
      etablissementId: number;
    }): Promise<Pub> => {
      const response = await axios.post<ApiResponse<Pub>>(
        `/pub/${id}/assign-etablissement`,
        { etablissement_id: etablissementId },
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pubKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: pubKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pubKeys.general() });
      // Le centre a été détaché côté API : les listes par centre bougent aussi,
      // et l'ancien centre n'est plus connu d'ici.
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'by-sign'] });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'by-etablissement'] });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'stats'] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ??
          i18n.t('pub.assignEtablissementError', "Failed to assign the ad to the network")
      );
    }
  });
};

/** Détache la campagne de son enseigne. Le rattachement centre est intact. */
export const useUnassignPubFromEtablissement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<Pub> => {
      const response = await axios.post<ApiResponse<Pub>>(
        `/pub/${id}/unassign-etablissement`,
      );
      return response.data.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: pubKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: pubKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pubKeys.general() });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'by-sign'] });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'by-etablissement'] });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'stats'] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ??
          i18n.t('pub.unassignEtablissementError', "Failed to detach the ad from its network")
      );
    }
  });
};

/**
 * Toggle pub active status
 */
export const useTogglePubActive = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number): Promise<Pub> => {
      const response = await axios.post<ApiResponse<Pub>>(`/pub/${id}/toggle-active`);
      return response.data.data;
    },
    onSuccess: (data, id) => {
      // Update specific pub in cache
      queryClient.invalidateQueries({ queryKey: pubKeys.detail(id) });
      
      // Invalidate lists to reflect active status changes
      queryClient.invalidateQueries({ queryKey: pubKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pubKeys.general() });
      queryClient.invalidateQueries({ queryKey: [...pubKeys.all, 'stats'] });
      
      if (data.sign_id) {
        queryClient.invalidateQueries({ queryKey: pubKeys.bySign(data.sign_id) });
      }
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ??
          i18n.t('pub.toggleError', "Failed to change the ad status")
      );
    }
  });
};